/**
 * ----------------------------------------------------------------------------------------------------------------------
 * FIND MY SIZE
 * ----------------------------------------------------------------------------------------------------------------------
 * Guided size funnel for the product page. Rendered by snippets/find-my-size.liquid, which supplies all
 * Liquid-derived data (ladder, fit key, translations) via a [data-fms-config] JSON block — read at event
 * time so behaviour survives Section Rendering API re-renders.
 *
 * Recommendation model, in one line:
 *
 *   target = usualSize + brandOffset + fitOffset + widthOffset   -> snapped to the nearest AVAILABLE rung
 *
 * For shoes the arithmetic happens in EU points, so real ladders with gaps (36.5 -> 37.5) snap correctly.
 * For apparel it happens in rung indexes (XXS..XXXXXL). Everything else is shared.
 *
 * Applying a recommendation checks the product's own option-value radio, so <variant-picker> reacts exactly
 * as if the shopper had tapped the size themselves.
 */
(function () {
  'use strict';

  var doc = document;
  var STORE_KEY = 'sneak:fms:v1';

  /* ==================== DEFAULTS (overridable via shop.metafields.custom.size_brand_offsets) ====================
   * How much LARGER a brand's printed size runs than the wearer's true foot size, in EU points.
   * Nike/Jordan run snug, so a Nike 43 belongs to a ~42.5 foot -> -0.5.
   * New Balance and Converse run generous -> +0.5.
   */
  /* ==================== BRANDS ====================
   * Every brand here is a footwear vendor this shop actually stocks — taken from the store's own
   * vendor list, so the funnel never asks about a brand we do not sell.
   *
   * THE NUMBER IS: how many EU sizes you go DOWN in this brand relative to Nike, for the same foot.
   *   +0.5  brand runs LARGE  -> take half a size less than your Nike size
   *    0    fits like Nike
   *   -0.5  brand runs SMALL  -> take half a size more than your Nike size
   *
   * Nike and Air Jordan are the reference at 0. Values follow the published sizing consensus (see
   * the sources in the notes accompanying this change); they are starting points, not measurements,
   * and per-model variation is real — an Adidas Samba runs small while a Gazelle runs large. Where
   * a specific product needs a different answer, give it a custom.size_tips rule: that always wins
   * over the brand value below. Override any of these per store via custom.size_brand_offsets.
   */
  var DEFAULT_BRAND_OFFSETS = {
    nike: 0,
    jordan: 0,
    adidas: 0.5,
    yeezy: -0.5,
    newbalance: 0.5,
    asics: 0,
    puma: 0,
    converse: 0.5,
    vans: 0,
    salomon: -0.5,
    saucony: 0,
    hoka: 0,
    onrunning: -0.5,
    reebok: -0.5,
    onitsuka: 0,
    /* Product-side only — see ASK_BRANDS. Nobody answers "I wear Birkenstock", but a
       Birkenstock product still has a tendency the conversion has to respect. */
    ugg: 0.5,
    crocs: 0.5,
    birkenstock: 0.5,
    timberland: 0.5,
    veja: -0.5,
    underarmour: 0,
    other: 0
  };

  /* Each brand's REAL size run, taken from this store's own products. Brands do not share a
   * ladder: Nike goes ...40, 40.5, 41, 42... with no 41.5, Adidas counts in thirds, New Balance
   * has 39.5 and 41.5 where Nike has neither. Offering a generic half-step grid asked shoppers to
   * pick sizes their brand does not make. Falls back to a generic run for a brand not listed.
   */
  var BRAND_LADDERS = {
    nike: [
        '35.5',
        '36',
        '36.5',
        '37.5',
        '38',
        '38.5',
        '39',
        '40',
        '40.5',
        '41',
        '42',
        '42.5',
        '43',
        '44',
        '44.5',
        '45',
        '45.5',
        '46',
        '47',
        '47.5',
        '48',
        '48.5'
    ],
    jordan: [
        '35.5',
        '36',
        '36.5',
        '37.5',
        '38',
        '38.5',
        '39',
        '40',
        '40.5',
        '41',
        '42',
        '42.5',
        '43',
        '44',
        '44.5',
        '45',
        '45.5',
        '46',
        '47',
        '47.5',
        '48',
        '48.5'
    ],
    adidas: [
        '35',
        '35.5',
        '36',
        '36 2/3',
        '37 1/3',
        '38',
        '38 2/3',
        '39 1/3',
        '40',
        '40 2/3',
        '41 1/3',
        '42',
        '42 2/3',
        '43 1/3',
        '44',
        '44 2/3',
        '45 1/3',
        '46',
        '46 2/3',
        '47 1/3',
        '48'
    ],
    yeezy: [
        '36',
        '37',
        '38',
        '39',
        '40.5',
        '42',
        '43',
        '44.5',
        '46',
        '47',
        '48.5'
    ],
    newbalance: [
        '35.5',
        '36',
        '37',
        '37.5',
        '38',
        '38.5',
        '39.5',
        '40',
        '40.5',
        '41.5',
        '42',
        '42.5',
        '43',
        '44',
        '44.5',
        '45',
        '45.5',
        '46.5',
        '47.5'
    ],
    asics: [
        '36',
        '37',
        '37.5',
        '38',
        '39',
        '39.5',
        '40',
        '40.5',
        '41.5',
        '42',
        '42.5',
        '43.5',
        '44',
        '44.5',
        '45',
        '46',
        '46.5',
        '47',
        '48'
    ],
    puma: [
        '35.5',
        '36',
        '37',
        '37.5',
        '38',
        '38.5',
        '39',
        '40',
        '40.5',
        '41',
        '42',
        '42.5',
        '43',
        '44',
        '44.5',
        '45',
        '46',
        '46.5',
        '47',
        '48.5'
    ],
    converse: [
        '35',
        '35.5',
        '36',
        '36.5',
        '37.5',
        '38',
        '38.5',
        '39',
        '40',
        '40.5',
        '41',
        '42',
        '42.5',
        '43',
        '44',
        '44.5',
        '45',
        '45.5',
        '46',
        '47',
        '47.5',
        '48',
        '48.5'
    ],
    vans: [
        '34.5',
        '35',
        '36',
        '36.5',
        '37',
        '38',
        '38.5',
        '39',
        '40',
        '40.5',
        '41',
        '42',
        '42.5',
        '43',
        '44',
        '44.5',
        '45',
        '46',
        '47',
        '48'
    ],
    salomon: [
        '36',
        '36 2/3',
        '37 1/3',
        '38',
        '38 2/3',
        '39 1/3',
        '40',
        '40 2/3',
        '41 1/3',
        '42',
        '42 2/3',
        '43 1/3',
        '44',
        '44 2/3',
        '45 1/3',
        '46',
        '46 2/3',
        '47 1/3',
        '48'
    ],
    hoka: [
        '36',
        '36 2/3',
        '37 1/3',
        '38',
        '38 2/3',
        '39 1/3',
        '40',
        '40 2/3',
        '41 1/3',
        '42',
        '42 2/3',
        '43 1/3',
        '44',
        '44 2/3',
        '45 1/3',
        '46',
        '46 2/3',
        '47 1/3',
        '48'
    ],
    onrunning: [
        '36',
        '36.5',
        '37',
        '37.5',
        '38',
        '38.5',
        '39',
        '40',
        '40.5',
        '41',
        '42',
        '42.5',
        '43'
    ],
    reebok: [
        '34',
        '34.5',
        '35',
        '36',
        '36.5',
        '37.5',
        '38.5',
        '39',
        '40',
        '40.5',
        '41',
        '42',
        '42.5',
        '43',
        '44',
        '44.5',
        '45',
        '45.5',
        '46',
        '47',
        '48.5'
    ],
    onitsuka: [
        '36',
        '37',
        '37.5',
        '38',
        '39',
        '39.5',
        '40',
        '40.5',
        '41.5',
        '42',
        '42.5',
        '43.5',
        '44',
        '44.5',
        '45',
        '46',
        '46.5',
        '47',
        '48'
    ],
    saucony: [
        '35.5',
        '36',
        '37',
        '37.5',
        '38',
        '38.5',
        '39',
        '40',
        '40.5',
        '41',
        '42',
        '42.5',
        '43',
        '44',
        '44.5',
        '45',
        '46',
        '46.5',
        '48'
    ]
};

  /* The brands the funnel actually ASKS about — a subset of the offsets table, because the table
     also carries product-side entries for brands no one names as their usual sneaker. */
  var ASK_BRANDS = [
    'nike', 'adidas', 'jordan', 'newbalance', 'yeezy', 'asics',
    'puma', 'converse', 'vans', 'salomon', 'saucony', 'hoka', 'onrunning', 'reebok', 'onitsuka'
  ];

  var BRAND_LABELS = {
    nike: 'Nike',
    adidas: 'Adidas',
    jordan: 'Air Jordan',
    newbalance: 'New Balance',
    yeezy: 'Yeezy',
    asics: 'ASICS',
    puma: 'Puma',
    converse: 'Converse',
    vans: 'Vans',
    salomon: 'Salomon',
    saucony: 'Saucony',
    hoka: 'Hoka',
    onrunning: 'On Running',
    reebok: 'Reebok',
    onitsuka: 'Onitsuka'
  };

  /* How the product itself fits, from the shared custom.size_tips metafield. Positive = size up. */
  var FIT_OFFSETS = {
    '1_bigger': 1,
    half_bigger: 0.5,
    regular: 0,
    half_smaller: -0.5,
    '1_smaller': -1
  };

  /* The EU run the "what size do you wear" grid always spans, whatever this product stocks. */
  var REFERENCE_MIN = 36;
  var REFERENCE_MAX = 48;

  var WIDTH_OFFSETS = { narrow: 0, normal: 0, wide: 0.5 };
  var FIT_PREF_OFFSETS = { tight: -0.5, regular: 0, oversized: 1 };

  /* ==================== HELPERS ==================== */

  function esc(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /**
   * Parse a size label to a number on the EU scale.
   *
   * Not parseFloat: the real catalogue has three shapes it gets wrong.
   *   "36 2/3"  Adidas / Salomon / Hoka use thirds -> parseFloat gives 36, losing 0.67 of a size
   *   "41-42"   Crocs uses ranges              -> parseFloat gives 41, ignoring the top half
   *   "42,5"    comma decimals                 -> parseFloat gives 42
   * " (GS)" suffixes and stray text are ignored.
   */
  function parseSize(label) {
    if (label == null) return NaN;
    var text = String(label).replace(/\(gs\)/i, '').replace(',', '.').trim();

    /* Whole-plus-fraction, e.g. "36 2/3" */
    var frac = text.match(/^(\d+)\s+(\d+)\s*\/\s*(\d+)/);
    if (frac) {
      var den = parseFloat(frac[3]);
      if (den) return parseFloat(frac[1]) + parseFloat(frac[2]) / den;
    }

    /* Range, e.g. "41-42" — take the midpoint, which is the foot it actually suits. */
    var range = text.match(/^(\d+(?:\.\d+)?)\s*[-–]\s*(\d+(?:\.\d+)?)/);
    if (range) return (parseFloat(range[1]) + parseFloat(range[2])) / 2;

    var plain = text.match(/-?\d+(?:\.\d+)?/);
    return plain ? parseFloat(plain[0]) : NaN;
  }

  /* Placeholders use the theme-wide %{name} form, which Shopify's `t` filter leaves intact. */
  function fill(template, values) {
    return String(template || '').replace(/%\{(\w+)\}/g, function (m, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : m;
    });
  }

  /**
   * x-drawer relocates itself to <body> the first time it opens, so a drawer and the mount that
   * owns it are no longer in the same subtree. Everything below therefore resolves by owner id
   * rather than by DOM containment.
   */
  var mounts = {};

  function ownerIdOf(root) {
    return root.getAttribute('data-fms-id');
  }

  function registerMount(root) {
    var id = ownerIdOf(root);
    if (id) mounts[id] = root;
  }

  function getRoot(el) {
    if (!el) return null;
    var direct = el.closest('[data-fms]');
    if (direct) return direct;
    /* Clicked inside a drawer that has been moved out to <body>. */
    var drawer = el.closest('[data-fms-drawer]');
    if (drawer) {
      var owner = drawer.getAttribute('data-fms-owner');
      if (owner && mounts[owner]) return mounts[owner];
      if (owner) {
        var found = doc.querySelector('[data-fms][data-fms-id="' + owner + '"]');
        if (found) {
          mounts[owner] = found;
          return found;
        }
      }
    }
    return null;
  }

  /** The drawer for a mount, wherever in the document it currently lives. */
  function getDrawer(root) {
    if (!root) return null;
    var id = ownerIdOf(root);
    if (id) {
      var byOwner = doc.querySelector('[data-fms-drawer][data-fms-owner="' + id + '"]');
      if (byOwner) return byOwner;
    }
    return root.querySelector('[data-fms-drawer]');
  }

  /** Query inside this mount's drawer — never inside the mount, which no longer contains it. */
  function inDrawer(root, selector) {
    var drawer = getDrawer(root);
    return drawer ? drawer.querySelector(selector) : null;
  }

  function getConfig(root) {
    if (!root) return null;
    if (!root._fmsConfig) {
      var node = root.querySelector('[data-fms-config]');
      if (!node) return null;
      try {
        root._fmsConfig = JSON.parse(node.textContent);
      } catch (e) {
        return null;
      }
    }
    /* Standalone mounts (e.g. the cart drawer) have no product and therefore no ladder. They exist
       to capture the shopper's size profile, which every product-bound mount then reuses. */
    if (root.hasAttribute('data-fms-standalone')) return root._fmsConfig;

    /* Inline mounts (bundle size panels) have no ladder at render time — the cells are fetched
       per colour on demand — so the ladder and fit rule are resolved from the DOM each time. */
    if (root.hasAttribute('data-fms-inline')) {
      var live = inlineContext(root, root._fmsConfig);
      if (!live) return null;
      return live;
    }
    return root._fmsConfig;
  }

  /* ==================== INLINE MOUNTS ==================== */

  /** The bundle panel this inline bar belongs to. */
  function inlineScope(root) {
    var sel = root.getAttribute('data-fms-scope');
    return (sel && root.closest(sel)) || root.parentElement;
  }

  /**
   * The size cells currently on screen. Bundles keep one hidden size group per colour in the
   * panel, so only the visible group is read — otherwise every colour's ladder would be merged.
   */
  function inlineCells(root) {
    var scope = inlineScope(root);
    if (!scope) return [];
    var sel = root.getAttribute('data-fms-cell-sel') || '[data-bd-size]';
    var all = Array.prototype.slice.call(scope.querySelectorAll(sel));
    var visible = all.filter(function (c) { return c.offsetParent !== null; });
    return visible.length ? visible : all;
  }

  /** Full product title for the active item, used to match the size_tips rules. */
  function inlineTitle(root) {
    var sel = root.getAttribute('data-fms-title-sel');
    var attr = root.getAttribute('data-fms-title-attr');
    if (sel && attr) {
      var scope = root.closest('[data-fms-title-root]') || doc;
      var card = scope.querySelector(sel);
      if (card && card.dataset[attr]) return card.dataset[attr];
    }
    return root.getAttribute('data-fms-title') || '';
  }

  /** Vendor of the item currently being sized — per-card in a deal, else the page's product. */
  function inlineVendor(root) {
    var attr = root.getAttribute('data-fms-vendor-attr');
    var sel = root.getAttribute('data-fms-title-sel');
    if (sel && attr) {
      var scope = root.closest('[data-fms-title-root]') || doc;
      var card = scope.querySelector(sel);
      if (card && card.dataset[attr]) return card.dataset[attr];
    }
    return root.getAttribute('data-fms-vendor') || '';
  }

  /** Resolve the keyword rules from custom.size_tips into a fit key. Mirrors the theme's matcher. */
  function fitKeyFor(title, rules) {
    /* null, not 'regular': "no rule matched" and "the merchant says true to size" are different
       answers — the first falls back to the brand's tendency, the second overrides it. */
    if (!rules) return null;
    title = String(title || '').toLowerCase();
    var entries = Object.keys(rules);
    for (var i = 0; i < entries.length; i++) {
      var keywords = entries[i].split(',');
      var allMatch = keywords.every(function (kw) {
        var t = kw.trim().toLowerCase();
        if (!t) return true;
        if (t.charAt(0) === '!') return title.indexOf(t.slice(1)) === -1;
        return title.indexOf(t) !== -1;
      });
      if (allMatch && FIT_OFFSETS[rules[entries[i]]] !== undefined) return rules[entries[i]];
    }
    return null;
  }

  function inlineContext(root, base) {
    var cells = inlineCells(root);
    if (!cells.length) return null;

    var labelAttr = root.getAttribute('data-fms-label-attr') || 'bdSizeLabel';
    var numeric = false;
    var ladder = cells.map(function (cell, i) {
      var label = (cell.dataset[labelAttr] || cell.textContent || '').trim();
      var num = parseSize(label);
      if (!isNaN(num)) numeric = true;
      return {
        label: label,
        value: isNaN(num) ? i : num,
        optionValueId: 'cell:' + i,
        available: !cell.disabled && !cell.classList.contains('is-disabled'),
        cell: cell
      };
    });
    /* A mixed run would put letters and numbers in one space — fall back to index order. */
    if (numeric && ladder.some(function (r) { return isNaN(parseSize(r.label)); })) {
      numeric = false;
      ladder.forEach(function (r, i) { r.value = i; });
    }

    var title = inlineTitle(root);
    var fitKey = fitKeyFor(title, base.rules);
    var ctx = {};
    for (var k in base) if (Object.prototype.hasOwnProperty.call(base, k)) ctx[k] = base[k];
    ctx.ladder = ladder;
    ctx.numeric = numeric;
    ctx.fitKey = fitKey;
    ctx.hasFitRule = fitKey !== null;
    ctx.productBrand = inlineVendor(root);
    ctx.fitLabel = (fitKey && base.tips && base.tips[fitKey]) || '';
    ctx.inline = true;
    return ctx;
  }

  /**
   * Passive pass: once the shopper has answered, mark the recommended cell in a bundle panel and
   * show the size on the bar. Recomputed per item, so a loose tee and a snug slide differ.
   */
  function decorateInline(root) {
    var cfg = getConfig(root);
    if (!cfg) return;
    var saved = usableProfile(cfg, loadSaved()[cfg.category]);
    if (!saved) return;

    var result = recommend(cfg, saved);
    var cells = inlineCells(root);
    var wanted = result.soldOut || !result.rung ? null : result.rung;
    var lead = root.querySelector('[data-fms-lead]');
    var wantedText = leadText(cfg, lead, wanted && wanted.label);

    /* Idempotence is what keeps this safe: decorating writes inside the panel that the click
       handlers and re-decoration passes also read, so a no-op pass must touch nothing at all.
       Bail out before writing when the DOM already says what we want it to say. */
    var currentMark = null;
    for (var i = 0; i < cells.length; i++) {
      if (cells[i].classList.contains('is-fms-pick')) { currentMark = cells[i]; break; }
    }
    var leadMatches = !lead || lead.textContent === wantedText;
    if (currentMark === (wanted ? wanted.cell : null) && leadMatches) {
      root._fmsSaved = saved;
      return;
    }

    for (var j = 0; j < cells.length; j++) cells[j].classList.remove('is-fms-pick');
    setAnswered(root, cfg, wanted ? wanted.label : null);
    if (!wanted) return;
    if (wanted.cell) wanted.cell.classList.add('is-fms-pick');
    root._fmsSaved = saved;
  }

  /**
   * The trigger's wording, given a recommendation. Swapping the prompt for the answer beats
   * appending it: "Find my size 43" reads as two fragments, "Your size: 43" reads as a sentence —
   * and stays one in every language, because the whole phrase is a single translated string.
   * The original prompt is remembered so the trigger can go back to it.
   */
  /**
   * Swap the line between its two states. Unanswered it asks a question and offers the link;
   * answered it states the recommendation and offers "change" — so the sentence always reads
   * correctly rather than being a control with a value bolted on.
   */
  function setAnswered(root, cfg, size) {
    var line = root.querySelector('.fms-line');
    var lead = root.querySelector('[data-fms-lead]');
    var action = root.querySelector('[data-fms-open]');
    var change = root.querySelector('[data-fms-change]');
    var answered = size != null;

    if (line) line.classList.toggle('is-answered', answered);
    if (lead) {
      if (lead._fmsPrompt == null) lead._fmsPrompt = lead.textContent;
      /* savedPrefix is the fallback so a missing string can never blank the sentence. */
      var answeredTpl = cfg.i18n.lineAnswered || cfg.i18n.savedPrefix || '%{size}';
      lead.textContent = answered ? fill(answeredTpl, { size: size }) : lead._fmsPrompt;
    }
    if (action) action.hidden = answered;
    if (change) change.hidden = !answered;
  }

  function leadText(cfg, lead, size) {
    if (!lead) return '';
    if (lead._fmsPrompt == null) lead._fmsPrompt = lead.textContent;
    var tpl = cfg.i18n.lineAnswered || cfg.i18n.savedPrefix || '%{size}';
    return size == null ? lead._fmsPrompt : fill(tpl, { size: size });
  }

  function decorateAllInline() {
    var roots = doc.querySelectorAll('[data-fms-inline]');
    for (var i = 0; i < roots.length; i++) decorateInline(roots[i]);
  }

  /**
   * Bundle size cells arrive asynchronously (a fetch per colour) and are replaced when the shopper
   * switches colour. Rather than watching the DOM — a MutationObserver here writes into the very
   * subtree it observes, which is a feedback loop waiting to happen — we re-decorate a couple of
   * beats after the only thing that can change the ladder: a click in the bundle UI.
   */
  var inlineTimers = [];
  function scheduleInlineDecorate() {
    for (var i = 0; i < inlineTimers.length; i++) clearTimeout(inlineTimers[i]);
    inlineTimers = [
      setTimeout(decorateAllInline, 120),
      setTimeout(decorateAllInline, 400),
      setTimeout(decorateAllInline, 900)
    ];
  }

  /** "New Balance" / "Air Jordan" -> the flat keys the offsets table uses. */
  function normaliseBrand(name) {
    if (!name) return '';
    var key = String(name).toLowerCase().replace(/[^a-z0-9]/g, '');
    if (key === 'airjordan' || key === 'jordanbrand') return 'jordan';
    return key;
  }

  function brandOffsets(cfg) {
    var merged = {};
    var key;
    for (key in DEFAULT_BRAND_OFFSETS) {
      if (Object.prototype.hasOwnProperty.call(DEFAULT_BRAND_OFFSETS, key)) merged[key] = DEFAULT_BRAND_OFFSETS[key];
    }
    if (cfg.brandOffsets && typeof cfg.brandOffsets === 'object') {
      for (key in cfg.brandOffsets) {
        if (Object.prototype.hasOwnProperty.call(cfg.brandOffsets, key)) {
          var num = parseFloat(cfg.brandOffsets[key]);
          if (!isNaN(num)) merged[String(key).toLowerCase()] = num;
        }
      }
    }
    return merged;
  }

  /* Persisted answers, so a returning shopper never fills the funnel twice. */
  function loadSaved() {
    try {
      return JSON.parse(localStorage.getItem(STORE_KEY)) || {};
    } catch (e) {
      return {};
    }
  }

  function saveAnswers(cfg, state) {
    try {
      var all = loadSaved();
      all[cfg.category] = {
        gender: state.gender,
        brand: state.brand,
        size: state.size,
        sizeLabel: state.sizeLabel,
        sizeNumeric: state.sizeNumeric,
        width: state.width,
        fitPref: state.fitPref
      };
      localStorage.setItem(STORE_KEY, JSON.stringify(all));
    } catch (e) {
      /* private mode / quota — the funnel still works, it just won't be remembered */
    }
  }

  /**
   * Clear the stored answers for a category and return every mount on the page to its unanswered
   * state — prompt text back, recommendation ring off. This is what "start over" means.
   */
  function forgetProfile(category) {
    try {
      var all = loadSaved();
      delete all[category];
      localStorage.setItem(STORE_KEY, JSON.stringify(all));
    } catch (e) {
      /* private mode — nothing was stored to begin with */
    }

    var roots = doc.querySelectorAll('[data-fms]');
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      var cfg = getConfig(root);
      if (!cfg || cfg.category !== category) continue;

      root._fmsSaved = null;
      setAnswered(root, cfg, null);

      if (root.hasAttribute('data-fms-inline')) {
        var cells = inlineCells(root);
        for (var j = 0; j < cells.length; j++) cells[j].classList.remove('is-fms-pick');
      }
    }
  }

  /**
   * Re-express a stored profile in THIS product's size space, or refuse it.
   *
   * "clothing" covers both an XXS..XXL hoodie (where the stored number is a rung index) and a
   * two-size sock (where labels parse to 35/40). Reusing the raw number across those gave nonsense:
   * a hoodie M (index 3) came out as the smallest sock, and a sock S (35) came out as XXL. When the
   * spaces do not line up we return null and simply ask again.
   */
  function usableProfile(cfg, saved) {
    if (!saved || typeof saved.size !== 'number') return null;

    /* Answered in a different space (index vs EU number) — not comparable. */
    if (typeof saved.sizeNumeric === 'boolean' && saved.sizeNumeric !== !!cfg.numeric) return null;

    if (!cfg.numeric) {
      /* Rung indexes only mean something on the ladder they came from, so re-find the label. */
      if (!saved.sizeLabel) return null;
      var want = String(saved.sizeLabel).trim().toLowerCase();
      for (var i = 0; i < (cfg.ladder || []).length; i++) {
        if (String(cfg.ladder[i].label).trim().toLowerCase() === want) {
          var byLabel = {};
          for (var k in saved) if (Object.prototype.hasOwnProperty.call(saved, k)) byLabel[k] = saved[k];
          byLabel.size = cfg.ladder[i].value;
          return byLabel;
        }
      }
      return null;
    }

    /* Profiles saved before the space was recorded carry no marker. Rather than discard them, sanity
       check the number against this ladder: a legacy 43 on a 35-48 shoe run is clearly a real EU
       size, whereas a legacy 3 is a rung index that wandered in from a lettered ladder. */
    if (typeof saved.sizeNumeric !== 'boolean') {
      var vals = (cfg.ladder || []).map(function (r) { return r.value; }).filter(function (v) { return !isNaN(v); });
      if (!vals.length) return null;
      var lo = Math.min.apply(null, vals) - 4;
      var hi = Math.max.apply(null, vals) + 4;
      if (saved.size < lo || saved.size > hi) return null;
    }
    return saved;
  }

  /* ==================== RECOMMENDATION ==================== */

  /**
   * Reference ladder the shopper picks their usual size from. For shoes this is a full half-size
   * EU run (not the product's own ladder — they may wear a size this product doesn't stock).
   * For apparel it is the product's ladder, since letter runs are already universal.
   */
  function referenceLadder(cfg, brand) {
    if (!cfg.numeric) return cfg.ladder.map(function (r, i) { return { label: r.label, value: i }; });

    /* Ask in the shopper's own brand's sizes. */
    var own = brand && BRAND_LADDERS[normaliseBrand(brand)];
    if (own) {
      return own.map(function (l) { return { label: l, value: parseSize(l) }; });
    }

    var values = cfg.ladder.map(function (r) { return r.value; }).filter(function (v) { return !isNaN(v); });
    if (!values.length) return [];
    /* This question asks what the shopper wears, NOT what this product stocks — so the range has to
       cover them even when the product does not. An Air Jordan that stops at 44.5 must still let a
       46 answer, otherwise they cannot complete the funnel at all. */
    var min = Math.min(Math.min.apply(null, values), REFERENCE_MIN);
    var max = Math.max(Math.max.apply(null, values), REFERENCE_MAX);
    var out = [];
    for (var v = min; v <= max + 0.001; v += 0.5) {
      var rounded = Math.round(v * 2) / 2;
      out.push({ label: formatSize(rounded), value: rounded });
    }
    return out;
  }

  function formatSize(value) {
    return value % 1 === 0 ? String(value) : String(value);
  }

  /**
   * Snap a target to the nearest rung, preferring available ones. Ties round UP — a slightly roomy
   * shoe is a keeper, a slightly tight one is a return.
   */
  function snap(ladder, target, availableOnly) {
    var pool = ladder.filter(function (r) {
      return !isNaN(r.value) && (!availableOnly || r.available);
    });
    if (!pool.length) return null;
    var best = null;
    var bestDist = Infinity;
    for (var i = 0; i < pool.length; i++) {
      var dist = Math.abs(pool[i].value - target);
      if (dist < bestDist - 0.001 || (Math.abs(dist - bestDist) < 0.001 && pool[i].value > (best ? best.value : -Infinity))) {
        bestDist = dist;
        best = pool[i];
      }
    }
    return best;
  }

  /**
   * A size label means different things in different brands, so the conversion is two-sided:
   *
   *   trueFoot     = usualSize + delta[brand the shopper wears]
   *   productLabel = trueFoot  + productAdjust
   *
   * where delta is negative for a brand that runs small (its 43 fits a 42.5 foot).
   *
   * productAdjust is the product side of that conversion. A size_tips rule is the merchant's
   * explicit statement about THIS product and always wins; without one we fall back to the general
   * tendency of the product's own brand. Using only the shopper's side — which is what this did
   * originally — told a Nike-43 wearer to buy a Nike 42.5, because it silently assumed every
   * product was brand-neutral.
   */
  function recommend(cfg, state) {
    var offsets = brandOffsets(cfg);
    var target = state.size;
    var reasons = { brand: 0, fit: 0, width: 0, pref: 0 };

    if (cfg.category === 'sneakers') {
      var brandKey = state.brand ? String(state.brand).toLowerCase() : 'other';
      reasons.brand = typeof offsets[brandKey] === 'number' ? offsets[brandKey] : 0;
      reasons.width = WIDTH_OFFSETS[state.width] || 0;
    } else {
      reasons.pref = FIT_PREF_OFFSETS[state.fitPref] || 0;
    }

    if (cfg.hasFitRule && typeof FIT_OFFSETS[cfg.fitKey] === 'number') {
      reasons.fit = FIT_OFFSETS[cfg.fitKey];
    } else if (cfg.category === 'sneakers') {
      var productKey = normaliseBrand(cfg.productBrand);
      reasons.fit = productKey && typeof offsets[productKey] === 'number' ? -offsets[productKey] : 0;
    } else {
      reasons.fit = typeof FIT_OFFSETS[cfg.fitKey] === 'number' ? FIT_OFFSETS[cfg.fitKey] : 0;
    }

    target = target + reasons.brand + reasons.fit + reasons.width + reasons.pref;

    var ideal = snap(cfg.ladder, target, false);
    var pick = snap(cfg.ladder, target, true);

    return {
      target: target,
      rung: pick,
      idealRung: ideal,
      soldOut: !pick,
      snapped: !!(pick && ideal && pick.value !== ideal.value),
      reasons: reasons
    };
  }

  /* ==================== RENDERING ==================== */

  function steps(cfg) {
    var list = [];
    if (cfg.askGender) list.push('gender');
    if (cfg.category === 'sneakers') {
      list.push('brand', 'size');
      if (cfg.askWidth) list.push('width');
    } else {
      list.push('size', 'fitPref');
    }
    return list;
  }

  function setProgress(root, cfg, stepName) {
    var order = steps(cfg);
    var bar = inDrawer(root, '[data-fms-bar]');
    var stepEl = inDrawer(root, '[data-fms-step]');
    var index = order.indexOf(stepName);
    var pct = stepName === 'result' ? 100 : ((index + 1) / order.length) * 100;
    if (bar) bar.style.width = pct + '%';
    if (stepEl) {
      stepEl.textContent = stepName === 'result'
        ? cfg.i18n.done
        : fill(cfg.i18n.stepOf, { current: index + 1, total: order.length });
    }
  }

  function reducedMotion() {
    return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  /**
   * Swap the pane, easing the panel between the two step heights rather than snapping. Steps differ
   * by 100px+ (a nine-brand grid vs a three-option question), and an abrupt resize under an anchored
   * popover is what reads as "jumpy". Height is animated explicitly and released back to auto
   * afterwards so the panel still grows with its content.
   */
  function render(root, html) {
    var body = inDrawer(root, '[data-fms-body]');
    if (!body) return;

    var pane = '<div class="fms__pane">' + html + '</div>';
    var from = body.offsetHeight;
    body.innerHTML = pane;

    if (!from || reducedMotion()) {
      body.style.height = '';
      return;
    }

    var to = body.scrollHeight;
    if (Math.abs(to - from) < 2) {
      body.style.height = '';
      return;
    }

    body.style.height = from + 'px';
    body.classList.add('is-resizing');
    void body.offsetHeight; /* flush, so the browser has a start value to animate from */
    body.style.height = to + 'px';

    clearTimeout(body._fmsHeightTimer);
    body._fmsHeightTimer = setTimeout(function () {
      body.style.height = '';
      body.classList.remove('is-resizing');
      /* Re-check only once the real height has settled. */
      keepPanelInView(root);
    }, 240);
  }

  function optionButton(step, value, label, hint) {
    return (
      '<button type="button" class="fms__opt" data-fms-answer="' + esc(step) + '" data-fms-value="' + esc(value) + '">' +
      '<span>' + esc(label) + '</span>' +
      (hint ? '<span class="fms__opt-hint">' + esc(hint) + '</span>' : '') +
      '</button>'
    );
  }

  function backButton(cfg, target) {
    if (!target) return '';
    return '<button type="button" class="fms__link" data-fms-back="' + esc(target) + '">' + esc(cfg.i18n.back) + '</button>';
  }

  function head(title, sub) {
    return '<p class="fms__q">' + esc(title) + '</p>' + (sub ? '<p class="fms__q-sub">' + esc(sub) + '</p>' : '');
  }

  function showStep(root, cfg, state, stepName) {
    var order = steps(cfg);
    var prev = order[order.indexOf(stepName) - 1] || null;
    state.step = stepName;
    setProgress(root, cfg, stepName);

    if (stepName === 'gender') {
      render(root, head(cfg.i18n.genderTitle, cfg.i18n.genderSub) +
        '<div class="fms__opts">' +
        optionButton('gender', 'm', cfg.i18n.genderMale) +
        optionButton('gender', 'f', cfg.i18n.genderFemale) +
        optionButton('gender', 'x', cfg.i18n.genderOther) +
        '</div>' + backButton(cfg, prev));
      return;
    }

    if (stepName === 'brand') {
      /* When the shop metafield is set it is the source of truth for which brands to offer, so a
         merchant can add or drop one without a theme deploy. Order follows BRAND_ORDER where known
         so the familiar names stay at the top. */
      var offsets = brandOffsets(cfg);
      var keys = ASK_BRANDS.slice();
      if (cfg.askBrands && cfg.askBrands.length) {
        keys = cfg.askBrands.map(normaliseBrand).filter(Boolean);
      }
      keys = keys.filter(function (k) { return k !== 'other' && typeof offsets[k] === 'number'; });
      keys.sort(function (a, b) {
        var ia = ASK_BRANDS.indexOf(a), ib = ASK_BRANDS.indexOf(b);
        return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
      });
      var html = '';
      for (var i = 0; i < keys.length; i++) {
        html += optionButton('brand', keys[i], BRAND_LABELS[keys[i]] || cfg.brandLabels && cfg.brandLabels[keys[i]] || keys[i]);
      }
      html += optionButton('brand', 'other', cfg.i18n.brandOther);
      render(root, head(cfg.i18n.brandTitle, cfg.i18n.brandSub) +
        '<div class="fms__opts fms__opts--split">' + html + '</div>' + backButton(cfg, prev));
      return;
    }

    if (stepName === 'size') {
      var ref = referenceLadder(cfg, state.brand);
      var cells = ref.map(function (r) {
        return '<button type="button" class="fms__size" data-fms-answer="size" data-fms-value="' + r.value + '">' + esc(r.label) + '</button>';
      }).join('');
      var sub = cfg.category === 'sneakers' && state.brand && state.brand !== 'other'
        ? fill(cfg.i18n.sizeSubBrand, { brand: BRAND_LABELS[state.brand] || state.brand })
        : cfg.i18n.sizeSub;
      /* Numeric runs are dense, letter runs are wide — match the theme's own grid column counts. */
      var cols = cfg.numeric ? 5 : 4;
      render(root, head(cfg.i18n.sizeTitle, sub) +
        '<div class="fms__size-grid" style="--fms-grid-columns:' + cols + ';--fms-grid-columns-mobile:' + (cfg.numeric ? 4 : 3) + '">' + cells + '</div>' +
        backButton(cfg, prev));
      return;
    }

    if (stepName === 'width') {
      render(root, head(cfg.i18n.widthTitle, cfg.i18n.widthSub) +
        '<div class="fms__opts">' +
        optionButton('width', 'narrow', cfg.i18n.widthNarrow) +
        optionButton('width', 'normal', cfg.i18n.widthNormal, cfg.i18n.widthHint) +
        optionButton('width', 'wide', cfg.i18n.widthWide) +
        '</div>' + backButton(cfg, prev));
      return;
    }

    if (stepName === 'fitPref') {
      render(root, head(cfg.i18n.fitPrefTitle, cfg.i18n.fitPrefSub) +
        '<div class="fms__opts">' +
        optionButton('fitPref', 'tight', cfg.i18n.fitPrefTight) +
        optionButton('fitPref', 'regular', cfg.i18n.fitPrefRegular, cfg.i18n.widthHint) +
        optionButton('fitPref', 'oversized', cfg.i18n.fitPrefOversized) +
        '</div>' + backButton(cfg, prev));
      return;
    }

    if (stepName === 'result') showResult(root, cfg, state);

    /* Hold the panel where it is; only rescue it if the new step overflows the window. */
    if (isPanelMode(root)) scrollPanelIntoView(getDrawer(root));
    else keepPanelInView(root);
  }

  function explain(cfg, state, result) {
    var parts = [];
    var r = result.reasons;

    if (cfg.category === 'sneakers' && r.brand !== 0 && state.brand && state.brand !== 'other') {
      parts.push(fill(cfg.i18n.whyBrand, { brand: BRAND_LABELS[state.brand] || state.brand }));
    }

    /* Two different reasons can move the size, and saying the wrong one is worse than saying
       nothing: a merchant fit rule ("this hoodie is a loose fit") reads completely differently from
       a brand tendency ("Adidas runs a little large"). Before this split, an Adidas shoe with no
       rule announced "this item is true to size, so we sized down", which contradicts itself. */
    if (cfg.hasFitRule) {
      if (r.fit > 0) parts.push(fill(cfg.i18n.whyFitBigger, { fit: cfg.fitLabel }));
      else if (r.fit < 0) parts.push(fill(cfg.i18n.whyFitSmaller, { fit: cfg.fitLabel }));
      else parts.push(fill(cfg.i18n.whyFitRegular, { fit: cfg.fitLabel }));
    } else if (r.fit !== 0 && cfg.productBrand) {
      /* r.fit is the negated brand offset, so positive here means the product's brand runs small. */
      parts.push(fill(r.fit > 0 ? cfg.i18n.whyBrandSmall : cfg.i18n.whyBrandLarge, { brand: cfg.productBrand }));
    }

    /* Only mention width when it actually moved the size. A narrow foot adds nothing, so the old
       trailing "this size will sit snugly" line was filler tacked onto the end of every result. */
    if (r.width > 0) parts.push(cfg.i18n.whyWide);

    if (r.pref > 0) parts.push(cfg.i18n.whyOversized);
    else if (r.pref < 0) parts.push(cfg.i18n.whyTight);

    if (result.snapped) parts.push(cfg.i18n.whySnapped);

    return parts.join(' ');
  }

  function showResult(root, cfg, state) {
    setProgress(root, cfg, 'result');
    saveAnswers(cfg, state);

    /* No product here, so there is no size to recommend — confirm the profile instead. It is what
       makes every product page and bundle able to answer without asking again. */
    if (cfg.standalone) {
      var ownLabel = String(state.size);
      for (var i = 0; i < (cfg.ladder || []).length; i++) {
        if (cfg.ladder[i].value === state.size) { ownLabel = cfg.ladder[i].label; break; }
      }
      render(root,
        '<div class="fms__result">' +
        '<p class="fms__result-label">' + esc(cfg.i18n.resultBadge) + '</p>' +
        '<p class="fms__result-size">' + esc(ownLabel) + '</p>' +
        '<p class="fms__why">' + esc(cfg.i18n.profileBody) + '</p>' +
        '<button type="button" class="fms__cta" data-fms-close>' + esc(cfg.i18n.profileDone) + '</button>' +
        '<button type="button" class="fms__link" data-fms-restart>' + esc(cfg.i18n.restart) + '</button>' +
        '</div>');
      hydrateSaved(root);
      return;
    }

    var result = recommend(cfg, state);
    state.result = result;

    if (result.soldOut) {
      render(root,
        '<div class="fms__result">' +
        '<p class="fms__result-label">' + esc(cfg.i18n.resultSoldOut) + '</p>' +
        '<p class="fms__why">' + esc(cfg.i18n.resultSoldOutBody) + '</p>' +
        '<button type="button" class="fms__link" data-fms-restart>' + esc(cfg.i18n.restart) + '</button>' +
        '</div>');
      return;
    }

    var label = result.rung.label;

    render(root,
      '<div class="fms__result">' +
      '<p class="fms__result-label">' + esc(cfg.i18n.resultBadge) + '</p>' +
      '<p class="fms__result-size">' + esc(label) + '</p>' +
      (function () {
        var why = explain(cfg, state, result);
        return why ? '<p class="fms__why">' + esc(why) + '</p>' : '';
      })() +
      '<button type="button" class="fms__cta" data-fms-apply="' + esc(result.rung.optionValueId) + '">' +
      esc(fill(cfg.i18n.resultCta, { size: label })) +
      '</button>' +
      '<button type="button" class="fms__link" data-fms-restart>' + esc(cfg.i18n.restart) + '</button>' +
      (cfg.socialProof ? '<p class="fms__result-social">' + esc(cfg.socialProof) + '</p>' : '') +
      '</div>');
  }

  /* ==================== APPLY ==================== */

  /** Check the product's own size radio so <variant-picker> updates price, media and ATC state. */
  function applySize(root, cfg, optionValueId) {
    /* Inline mounts drive the bundle's own size buttons — clicking one keeps the bundle's
       selection, counters and CTA logic entirely in bundle-deals.js / bundle-tiers.js. */
    if (cfg.inline) {
      var match = null;
      for (var n = 0; n < cfg.ladder.length; n++) {
        if (cfg.ladder[n].optionValueId === optionValueId) { match = cfg.ladder[n]; break; }
      }
      if (!match || !match.cell || !match.available) return false;
      match.cell.click();
      return true;
    }

    var name = cfg.formId + '-option' + cfg.optionPosition;
    var selector = 'input[name="' + (window.CSS && CSS.escape ? CSS.escape(name) : name) + '"]';
    var inputs = doc.querySelectorAll(selector);
    for (var i = 0; i < inputs.length; i++) {
      if (String(inputs[i].value) !== String(optionValueId)) continue;
      if (inputs[i].disabled) return false;
      inputs[i].checked = true;
      inputs[i].dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }
    return false;
  }

  function openPurchaseOverlay() {
    var trigger = doc.querySelector('[data-purchase-overlay-trigger]');
    if (trigger) trigger.click();
  }

  /**
   * The panel lives inside the purchase overlay's own scroll area, so on a phone it can open below
   * the fold — which is what made it feel unreachable. Bring it into view instead of promoting it
   * to a floating popup, which fought the overlay's stacking context and washed the whole thing out.
   */
  function scrollPanelIntoView(panel) {
    if (!panel || !panel.scrollIntoView) return;
    var smooth = !reducedMotion();
    setTimeout(function () {
      try {
        panel.scrollIntoView({ block: 'nearest', behavior: smooth ? 'smooth' : 'auto' });
      } catch (e) {
        panel.scrollIntoView();
      }
    }, 30);
  }

  function isPanelMode(root) {
    return !!(root && root.hasAttribute('data-fms-in-overlay'));
  }

  function closeDrawer(root) {
    var drawer = getDrawer(root);
    if (!drawer) return;
    if (isPanelMode(root)) {
      drawer.hidden = true;
      var card = root.querySelector('.fms-line');
      if (card) card.hidden = false;
      return;
    }
    if (drawer.hide) drawer.hide();
    else drawer.removeAttribute('open');
  }

  function openDrawer(root) {
    var drawer = getDrawer(root);
    if (!drawer) return;
    if (isPanelMode(root)) {
      /* The questions take the card's place rather than floating over it. */
      drawer.hidden = false;
      var card = root.querySelector('.fms-line');
      if (card) card.hidden = true;
      scrollPanelIntoView(drawer);
      return;
    }
    if (drawer.show) drawer.show();
    else drawer.setAttribute('open', '');
  }

  /* ==================== DESKTOP ANCHORING ==================== */

  var DESKTOP = '(min-width: 700px)';

  function isDesktop() {
    return !!(window.matchMedia && window.matchMedia(DESKTOP).matches);
  }

  function clearAnchor(drawer) {
    if (!drawer) return;
    drawer.style.left = '';
    drawer.style.top = '';
    drawer.style.right = '';
    drawer.style.bottom = '';
  }

  var MARGIN = 16;
  var GAP = 8;

  /**
   * On desktop the theme's drawer base pins itself to the right edge of the window, which is a long
   * way from the button that opened it. Anchor the panel to the trigger instead, flipping above it
   * when there is not enough room below. Mobile keeps the native bottom sheet.
   *
   * Only ever called when the trigger may have MOVED (open, scroll, resize). Advancing a step must
   * not re-run this: each step is a different height, and recomputing would shunt the panel around
   * under the reader's cursor. Step changes go through keepPanelInView instead.
   */
  /**
   * The control the panel should hang off. Once answered, the "find my size" button is hidden and
   * "change answers" is the visible one — measuring the hidden button gave a 0x0 rect, and the
   * fallback left the fixed drawer with no top, so it landed at its static position far down a long
   * page: the screen dimmed and the funnel was nowhere on screen.
   */
  function anchorEl(root) {
    var candidates = root.querySelectorAll('[data-fms-open], [data-fms-change]');
    for (var i = 0; i < candidates.length; i++) {
      var r = candidates[i].getBoundingClientRect();
      if (r.width || r.height) return candidates[i];
    }
    return null;
  }

  /** Last resort: centre the panel rather than let it inherit a static position off-screen. */
  function centreAnchor(drawer) {
    var width = drawer.offsetWidth || 420;
    var height = drawer.offsetHeight || 0;
    drawer.style.right = 'auto';
    drawer.style.bottom = 'auto';
    drawer.style.left = Math.round(Math.max(MARGIN, (window.innerWidth - width) / 2)) + 'px';
    drawer.style.top = Math.round(Math.max(MARGIN, (window.innerHeight - height) / 2)) + 'px';
  }

  function anchorToTrigger(root) {
    var drawer = getDrawer(root);
    if (!drawer) return;
    /* Panel mode is laid out by the overlay, not positioned by us. */
    if (isPanelMode(root)) return;
    if (!isDesktop()) {
      clearAnchor(drawer);
      return;
    }

    var trigger = anchorEl(root);
    if (!trigger) {
      centreAnchor(drawer);
      return;
    }

    var t = trigger.getBoundingClientRect();
    var margin = MARGIN;
    var gap = GAP;
    var width = drawer.offsetWidth || 420;
    var height = drawer.offsetHeight || 0;

    /* If the trigger itself is off-screen — it lives inside the purchase overlay, which relocates
       and animates as it opens — anchoring to it puts the panel somewhere nobody can see. Fall back
       to the drawer's own layout instead of guessing. */
    /* Trigger scrolled out of view: centre rather than clear, because clearing leaves this fixed
       drawer with no top and it falls back to its static position, which on a long page is well
       below the fold. */
    if (t.bottom < 0 || t.top > window.innerHeight) {
      centreAnchor(drawer);
      return;
    }

    var left = Math.max(margin, Math.min(t.left, window.innerWidth - width - margin));
    var top = t.bottom + gap;
    if (height && top + height > window.innerHeight - margin) {
      var above = t.top - gap - height;
      top = above >= margin ? above : Math.max(margin, window.innerHeight - height - margin);
    }
    /* Clamp unconditionally. The guarded branch above is skipped when height reads 0 (a drawer
       measured mid-open), and without this the panel was landing thousands of pixels down the page:
       the screen dimmed and the funnel was nowhere to be seen. */
    var maxTop = Math.max(margin, window.innerHeight - (height || 0) - margin);
    top = Math.min(Math.max(margin, top), maxTop);

    drawer.style.right = 'auto';
    drawer.style.bottom = 'auto';
    drawer.style.left = Math.round(left) + 'px';
    drawer.style.top = Math.round(top) + 'px';
  }

  /**
   * After a step swap the panel is a different height but the trigger has not moved, so its top
   * stays exactly where it is — the reader's eye is already there. The only adjustment is pulling
   * it back up when the taller step would now run off the bottom of the window.
   */
  /**
   * x-drawer writes its own inline left/right/top while opening, and does not reliably emit
   * dialog:after-show (it fires for some drawers on this theme and not others). So rather than
   * depending on that event, re-assert the anchor over the first few frames of the open — the
   * writes are idempotent, so the extra passes cost nothing and the last word is always ours.
   */
  function scheduleAnchor(root) {
    anchorToTrigger(root);
    requestAnimationFrame(function () { anchorToTrigger(root); });
    setTimeout(function () { anchorToTrigger(root); }, 60);
    setTimeout(function () { anchorToTrigger(root); }, 180);
  }

  function keepPanelInView(root) {
    var drawer = getDrawer(root);
    if (!drawer || !isDesktop()) return;
    var top = parseFloat(drawer.style.top);
    if (isNaN(top)) return;
    var height = drawer.offsetHeight || 0;
    if (!height) return;

    var maxTop = window.innerHeight - height - MARGIN;
    if (top > maxTop) drawer.style.top = Math.round(Math.max(MARGIN, maxTop)) + 'px';
  }

  /* ==================== SAVED-ANSWER SHORTCUT ==================== */

  /**
   * If this shopper already completed the funnel for this category, show the recommendation for THIS
   * product straight on the trigger — the offsets are per-product, so the answer is recomputed, never
   * just replayed.
   */
  function hydrateSaved(root) {
    var cfg = getConfig(root);
    if (!cfg) return;
    var saved = usableProfile(cfg, loadSaved()[cfg.category]);
    if (!saved) return;

    var label;
    if (cfg.standalone) {
      /* No product to correct for — echo back the size they told us. */
      label = String(saved.size);
      for (var i = 0; i < (cfg.ladder || []).length; i++) {
        if (cfg.ladder[i].value === saved.size) { label = cfg.ladder[i].label; break; }
      }
    } else {
      var result = recommend(cfg, saved);
      if (result.soldOut) return;
      label = result.rung.label;
    }

    setAnswered(root, cfg, label);

    root._fmsSaved = saved;
  }

  /* ==================== EVENTS ==================== */

  function stateFor(root) {
    if (!root._fmsState) root._fmsState = { gender: null, brand: null, size: null, width: null, fitPref: null };
    return root._fmsState;
  }

  /**
   * A click on a bundle colour card swaps the whole size ladder (and fetches it), so re-run the
   * passive decoration a few beats later. Capture phase, so it is queued before the bundle's own
   * handlers have finished rearranging the panel.
   */
  doc.addEventListener('click', function (e) {
    var target = e.target;
    if (!(target instanceof Element)) return;
    if (!doc.querySelector('[data-fms-inline]')) return;
    if (target.closest('[data-bd-card], [data-bt-card], [data-bd-panel-close], [data-bt-panel-close]')) {
      scheduleInlineDecorate();
    }
  }, true);

  doc.addEventListener('click', function (e) {
    var target = e.target;
    if (!(target instanceof Element)) return;

    /* Open */
    var opener = target.closest('[data-fms-open]');
    if (opener) {
      var root = getRoot(opener);
      var cfg = getConfig(root);
      if (!cfg) return;
      var state = stateFor(root);
      /* Returning shopper: jump straight to their recomputed result. */
      if (root._fmsSaved) {
        root._fmsState = {
          gender: root._fmsSaved.gender || null,
          brand: root._fmsSaved.brand || null,
          size: root._fmsSaved.size,
          width: root._fmsSaved.width || null,
          fitPref: root._fmsSaved.fitPref || null
        };
        openDrawer(root);
        showStep(root, cfg, root._fmsState, 'result');
      } else {
        root._fmsState = { gender: null, brand: null, size: null, width: null, fitPref: null };
        openDrawer(root);
        showStep(root, cfg, root._fmsState, steps(cfg)[0]);
      }
      opener.setAttribute('aria-expanded', 'true');
      scheduleAnchor(root);
      return;
    }

    /* Answer a step */
    var answer = target.closest('[data-fms-answer]');
    if (answer) {
      var aRoot = getRoot(answer);
      var aCfg = getConfig(aRoot);
      if (!aCfg) return;
      var aState = stateFor(aRoot);
      var key = answer.dataset.fmsAnswer;
      var raw = answer.dataset.fmsValue;
      aState[key] = key === 'size' ? parseFloat(raw) : raw;
      if (key === 'size') {
        /* A bare number is not portable between ladders: on XXS..XXL it is an index, on a numeric
           run it is an EU size. Remember the label and which space it came from so the profile can
           be reused safely — or refused — on a product that sizes differently. */
        aState.sizeLabel = (answer.textContent || '').trim();
        aState.sizeNumeric = !!aCfg.numeric;
      }

      var order = steps(aCfg);
      var next = order[order.indexOf(key) + 1] || 'result';
      showStep(aRoot, aCfg, aState, next);
      return;
    }

    /* Back */
    var back = target.closest('[data-fms-back]');
    if (back) {
      var bRoot = getRoot(back);
      var bCfg = getConfig(bRoot);
      if (bCfg) showStep(bRoot, bCfg, stateFor(bRoot), back.dataset.fmsBack);
      return;
    }

    /* "Change" — reopen the questions from the top. Deliberately does NOT clear the stored
       profile: abandoning halfway should leave the shopper with the answer they already had.
       The in-drawer "start over" is the one that forgets. */
    var change = target.closest('[data-fms-change]');
    if (change) {
      var chRoot = getRoot(change);
      var chCfg = getConfig(chRoot);
      if (!chCfg) return;
      chRoot._fmsState = { gender: null, brand: null, size: null, width: null, fitPref: null };
      openDrawer(chRoot);
      showStep(chRoot, chCfg, chRoot._fmsState, steps(chCfg)[0]);
      scheduleAnchor(chRoot);
      return;
    }

    /* Dismiss (standalone confirmation) */
    var closer = target.closest('[data-fms-close]');
    if (closer) {
      var cRoot = getRoot(closer);
      if (cRoot) closeDrawer(cRoot);
      return;
    }

    /* Restart */
    var restart = target.closest('[data-fms-restart]');
    if (restart) {
      var rRoot = getRoot(restart);
      var rCfg = getConfig(rRoot);
      if (!rCfg) return;
      /* A real reset, not just a rewind of this one drawer: forget the stored profile and strip the
         recommendation off every mount on the page. Otherwise the shopper re-answers here while the
         bundle rows behind them still point at the size they just rejected. */
      forgetProfile(rCfg.category);
      rRoot._fmsState = { gender: null, brand: null, size: null, width: null, fitPref: null };
      showStep(rRoot, rCfg, rRoot._fmsState, steps(rCfg)[0]);
      return;
    }

    /* Apply the recommendation */
    var apply = target.closest('[data-fms-apply]');
    if (apply) {
      var pRoot = getRoot(apply);
      var pCfg = getConfig(pRoot);
      if (!pCfg) return;
      var ok = applySize(pRoot, pCfg, apply.dataset.fmsApply);
      if (ok) {
        apply.textContent = pCfg.i18n.resultApplied;
        apply.classList.add('is-applied');
        if (pCfg.inline) {
          /* The shopper is already inside the bundle flow — close and let them keep picking. */
          setTimeout(function () {
            closeDrawer(pRoot);
            decorateInline(pRoot);
          }, 450);
        } else {
          hydrateSaved(pRoot);
          var inOverlay = pRoot.hasAttribute('data-fms-in-overlay');
          setTimeout(function () {
            closeDrawer(pRoot);
            /* Only hand off when the funnel sits OUTSIDE the purchase overlay. Inside it the
               overlay is already open behind us, and re-triggering it would toggle it shut. */
            if (!inOverlay) setTimeout(openPurchaseOverlay, 220);
          }, 550);
        }
      }
      return;
    }
  });

  /* Native drawer close event (bubbles) — keep the trigger's aria state honest. */
  doc.addEventListener('dialog:after-hide', function (e) {
    var target = e.target;
    if (!(target instanceof Element) || !target.hasAttribute('data-fms-drawer')) return;
    var root = getRoot(target);
    var opener = root ? root.querySelector('[data-fms-open]') : null;
    if (opener) opener.setAttribute('aria-expanded', 'false');
    /* Drop the inline anchoring so the drawer returns to its own layout next time. */
    clearAnchor(target);
  });

  /* x-drawer writes its own inline left/right/top when it opens, so anchoring has to happen
     after that — not inside the click handler, which runs first and gets overwritten. */
  doc.addEventListener('dialog:after-show', function (e) {
    var target = e.target;
    if (!(target instanceof Element) || !target.hasAttribute('data-fms-drawer')) return;
    var root = getRoot(target);
    if (root) anchorToTrigger(root);
  });

  /* Keep an open panel pinned to its trigger while the page moves under it. Throttled to one pass
     per frame — scroll fires far faster than the panel can usefully be repositioned, and doing the
     measure/write on every event is what makes it judder. */
  var reanchorQueued = false;
  function reanchor() {
    if (reanchorQueued) return;
    reanchorQueued = true;
    requestAnimationFrame(function () {
      reanchorQueued = false;
      var open = doc.querySelectorAll('x-drawer[data-fms-drawer][open]');
      for (var i = 0; i < open.length; i++) {
        var root = getRoot(open[i]);
        if (root) anchorToTrigger(root);
      }
    });
  }
  window.addEventListener('resize', reanchor, { passive: true });
  window.addEventListener('scroll', reanchor, { passive: true });

  /* ==================== INIT ==================== */

  /**
   * qty-breaks, bundle-tiers and bundle-deals each replace the normal variant picker with their own
   * size UI and hide the theme's. The product-page block sits outside that picker, so on those
   * products it survives as a second, redundant card whose "apply" would target radios nobody can
   * see. Whenever a surface-specific mount is present, it owns the page and the block stands down.
   */
  function hideRedundantBlockMount() {
    if (!doc.querySelector('[data-fms-inline]')) return;
    var blocks = doc.querySelectorAll('[data-fms]:not([data-fms-inline]):not([data-fms-standalone])');
    for (var i = 0; i < blocks.length; i++) blocks[i].hidden = true;
  }

  function init() {
    hideRedundantBlockMount();
    var roots = doc.querySelectorAll('[data-fms]');
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      registerMount(root);
      if (root.hasAttribute('data-fms-inline')) {
        root._fmsInit = true;
        decorateInline(root);
        continue;
      }
      if (root._fmsInit) continue;
      root._fmsInit = true;
      hydrateSaved(root);
    }
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', init);
  else init();

  /* Re-hydrate after Section Rendering API swaps (variant change re-renders product info). */
  doc.addEventListener('shopify:section:load', init);
  doc.addEventListener('variant:change', init);
})();
